import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeftRight, ClipboardCheck, Flag, Send, ShoppingCart } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { api, inr, num, actionTone, critTone } from "@/lib/mro";

export default function Recommendations({ plant }) {
  const [data, setData] = useState(null);
  const [action, setAction] = useState("all");
  const [criticality, setCriticality] = useState("all");
  const navigate = useNavigate();

  const load = () => {
    setData(null);
    api.get("/recommendations", { params: { plant, action, criticality } }).then((r) => setData(r.data));
  };

  useEffect(load, [plant, action, criticality]);

  const act = async (rec, verb, label) => {
    await api.post("/recommendations/action", {
      material_id: rec.material_id, action: verb,
      note: `${label} — ${rec.canonical_name} @ ${rec.plant}`,
      payload: { from: rec.current_safety_stock, to: rec.recommended_safety_stock },
    });
    toast.success(`${label} logged`, { description: `${rec.plant} · ${rec.material_code}` });
    load();
  };

  if (!data) return <div className="space-y-4">{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-40 rounded-xl" />)}</div>;

  return (
    <div className="space-y-5" data-testid="recommendations-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-extrabold tracking-tight">Safety-stock recommendations</h1>
          <p className="text-sm text-muted-foreground">Every number is explained — demand variability, lead time, vendor reliability, equipment criticality and network cover.</p>
        </div>
        <div className="flex gap-2">
          <Select value={action} onValueChange={setAction}>
            <SelectTrigger className="h-9 w-[150px] text-xs" data-testid="rec-action-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All actions</SelectItem>
              <SelectItem value="reduce">Reduce</SelectItem>
              <SelectItem value="retain">Retain</SelectItem>
              <SelectItem value="increase">Increase</SelectItem>
            </SelectContent>
          </Select>
          <Select value={criticality} onValueChange={setCriticality}>
            <SelectTrigger className="h-9 w-[150px] text-xs" data-testid="rec-crit-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All criticality</SelectItem>
              <SelectItem value="A">A — critical</SelectItem>
              <SelectItem value="B">B — important</SelectItem>
              <SelectItem value="C">C — routine</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-4" data-testid="rec-summary">
        {[["Reduce", data.summary.reduce, "text-emerald-500"], ["Retain", data.summary.retain, "text-slate-400"],
          ["Increase", data.summary.increase, "text-rose-500"], ["Capital release", inr(data.summary.total_release), "text-primary"]].map(([l, v, tone]) => (
          <div key={l} className="panel p-4">
            <div className="font-mono text-[10px] uppercase tracking-[0.16em] text-muted-foreground">{l}</div>
            <div className={`mt-1.5 font-display text-xl font-extrabold tabular ${tone}`}>{typeof v === "number" ? num(v) : v}</div>
          </div>
        ))}
      </div>

      <div className="space-y-4">
        {data.recommendations.slice(0, 30).map((r, i) => (
          <div key={r.material_id} className="panel reveal p-5" style={{ animationDelay: `${Math.min(i * 30, 300)}ms` }} data-testid={`rec-card-${r.material_id}`}>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <Badge variant="outline" className={actionTone(r.action)} data-testid={`rec-action-badge-${r.material_id}`}>{r.action}</Badge>
                  <Badge variant="outline" className={critTone(r.criticality)}>Criticality {r.criticality}</Badge>
                  <span className="font-mono text-[11px] text-muted-foreground">{r.material_code} · {r.plant}</span>
                </div>
                <h3 className="mt-2 font-display text-base font-bold">{r.canonical_name}</h3>
                <p className="mt-1 max-w-3xl text-sm text-muted-foreground" data-testid={`rec-narrative-${r.material_id}`}>{r.narrative}</p>
              </div>
              <div className="shrink-0 text-right">
                <div className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">Safety stock</div>
                <div className="font-display text-2xl font-extrabold tabular">
                  {r.current_safety_stock} <span className="text-muted-foreground">→</span> {r.recommended_safety_stock}
                </div>
                {r.working_capital_release > 0 && <div className="font-mono text-xs font-semibold text-emerald-500">{inr(r.working_capital_release)} released</div>}
              </div>
            </div>

            <div className="mt-4 grid gap-3 sm:grid-cols-3 lg:grid-cols-6">
              {[["On hand", `${r.on_hand} ${r.uom}`], ["Avg demand", `${r.avg_monthly_demand}/mo`], ["Std dev", r.demand_std_dev],
                ["Lead time", `${r.lead_time_days} d`], ["Service factor", r.service_factor], ["Network cover", `${r.network_alternate_qty} ${r.uom}`]].map(([l, v]) => (
                <div key={l} className="rounded-lg border border-border bg-muted/30 p-2.5">
                  <div className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">{l}</div>
                  <div className="font-mono text-sm font-semibold">{v}</div>
                </div>
              ))}
            </div>

            <ul className="mt-3 space-y-1 text-xs text-muted-foreground">
              {r.reasons.map((x, j) => <li key={j} className="flex gap-2"><span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-primary" />{x}</li>)}
            </ul>

            <div className="mt-4 flex flex-wrap gap-2">
              <Button size="sm" data-testid={`accept-${r.material_id}`} onClick={() => act(r, r.action, `Safety stock ${r.action}d to ${r.recommended_safety_stock}`)}>
                <ClipboardCheck className="mr-1.5 h-3.5 w-3.5" />Apply {r.action}
              </Button>
              {r.network_alternate_qty > 0 && (
                <Button size="sm" variant="outline" data-testid={`transfer-${r.material_id}`} onClick={() => act(r, "stock_transfer", "Stock transfer initiated")}>
                  <ArrowLeftRight className="mr-1.5 h-3.5 w-3.5" />Initiate stock transfer
                </Button>
              )}
              <Button size="sm" variant="outline" data-testid={`replenish-${r.material_id}`} onClick={() => act(r, "replenishment_request", "Replenishment request raised")}>
                <ShoppingCart className="mr-1.5 h-3.5 w-3.5" />Raise replenishment
              </Button>
              <Button size="sm" variant="outline" data-testid={`mark-critical-${r.material_id}`} onClick={() => act(r, "mark_critical", "Marked critical — no risk reduction")}>
                <Flag className="mr-1.5 h-3.5 w-3.5" />Mark critical
              </Button>
              <Button size="sm" variant="ghost" data-testid={`approval-${r.material_id}`} onClick={() => act(r, "sent_for_approval", "Sent for planner approval")}>
                <Send className="mr-1.5 h-3.5 w-3.5" />Send for planner approval
              </Button>
              <Button size="sm" variant="ghost" data-testid={`part360-${r.material_id}`} onClick={() => navigate(`/part/${r.material_id}`)}>Part 360</Button>
            </div>
            {r.decision_log && (
              <div className="mt-3 rounded-lg border border-border bg-muted/40 p-2.5 font-mono text-[11px] text-muted-foreground" data-testid={`rec-log-${r.material_id}`}>
                Last action: {r.decision_log.action} by {r.decision_log.actor} · {new Date(r.decision_log.created_at).toLocaleString()}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
